// NotesTab — a SINGLE editable note body per lead. Not an append-only feed.
//
// Design decision (2026-08-04): the previous version had two different inputs
// storing to two different backends:
//   1. Leads-list card inline textarea  → `lead.quickNote` (single string, PUT /leads/:id)
//   2. Lead Detail "Add a note" composer → `lead.notes[]`  (append list, POST /leads/:id/notes)
// Users found this confusing: editing the box in one place spawned a new note
// entry rather than updating the existing text. Fixed by consolidating both
// UIs onto `quickNote` as the shared, always-editable body. The `notes[]`
// array is still used for SYSTEM-generated activity entries (AI SMS logs,
// disposition changes, etc.) and is shown as a read-only "Activity" timeline
// below the editable box so no audit history disappears from view.
//
// UX matches the Leads-list card: 1.2s debounce during typing, save-on-blur,
// small status pill so the agent sees when the change hit the server. Cmd/
// Ctrl+S also flushes an immediate save for keyboard-driven agents.

import React, { useEffect, useRef, useState } from 'react';
import { StickyNote, Check, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPut } from '../../../lib/api.js';
import { fmtFromNow } from '../../../lib/format.js';

const SAVE_DEBOUNCE_MS = 1200;

export default function NotesTab({ leadId, lead, onChange }) {
  const [val, setVal] = useState(lead?.quickNote || '');
  const [state, setState] = useState('idle'); // 'idle'|'typing'|'saving'|'saved'|'error'
  const initialRef = useRef(lead?.quickNote || '');
  const timerRef = useRef(null);

  // If the server pushes a new value (another agent edited, a webhook wrote
  // to quickNote, etc.), pull it in — but only while we're not in the middle
  // of typing/saving, so we don't clobber an in-progress edit.
  useEffect(() => {
    if (state === 'idle' && (lead?.quickNote || '') !== initialRef.current) {
      setVal(lead?.quickNote || '');
      initialRef.current = lead?.quickNote || '';
    }
  }, [lead?.quickNote]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(next) {
    setState('saving');
    try {
      await apiPut(`/leads/${leadId}`, { quickNote: next });
      initialRef.current = next;
      setState('saved');
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1500);
      onChange?.();
    } catch (e) {
      setState('error');
      toast.error(e.response?.data?.error || 'Failed to save note');
    }
  }

  function onChangeText(e) {
    const next = e.target.value;
    setVal(next);
    setState('typing');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(next), SAVE_DEBOUNCE_MS);
  }

  function onBlur() {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val !== initialRef.current) save(val);
  }

  // Cmd/Ctrl+S flushes an immediate save without waiting for the debounce.
  function onKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      if (timerRef.current) clearTimeout(timerRef.current);
      if (val !== initialRef.current) save(val);
    }
  }

  // Read-only activity feed. Existing `notes[]` entries stay visible so no
  // audit history is lost, but they're clearly labeled "Activity" — the
  // composer is not what writes to them anymore.
  const activity = (lead?.notes || []).slice().reverse();

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      {/* THE single editable note body. This is the same field the Leads-list
          card edits, so writes here immediately flow to that card and vice
          versa. */}
      <div className="rounded-xl bg-civix-panel border border-civix-border p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/50">
            <StickyNote className="w-3.5 h-3.5" /> Notes
          </div>
          <SaveStatus state={state} />
        </div>
        <textarea
          value={val}
          onChange={onChangeText}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder="What happened on this call/text? Callback times, objections, family details… Auto-saves as you type."
          rows={8}
          className="w-full resize-y min-h-[160px] rounded-lg bg-civix-bg border border-civix-border
                     px-3 py-2 text-sm text-white/90 focus:border-civix-purple/60 outline-hidden
                     placeholder:text-white/30"
        />
        <div className="mt-2 text-[10px] text-white/40">
          Auto-saves 1.2s after you stop typing · ⌘/Ctrl + S to save now · Shows in the Leads list card too
        </div>
      </div>

      {/* Activity feed — system-generated entries only (AI SMS, disposition
          changes, imports). NOT a manual note history agents have to manage. */}
      {activity.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40 px-1">
            Activity
          </div>
          {activity.map((n, i) => (
            <div
              key={n._id || i}
              className="rounded-lg bg-civix-panel border border-civix-border px-3 py-2.5"
            >
              <div className="text-sm text-white/90 whitespace-pre-wrap wrap-break-word">{n.body}</div>
              <div className="mt-1.5 text-[11px] text-white/45 flex items-center gap-2">
                <span className="font-medium text-white/60">{n.authorName || 'System'}</span>
                <span>·</span>
                <span>{fmtFromNow(n.createdAt)}</span>
                {n.type && n.type !== 'note' && (
                  <>
                    <span>·</span>
                    <span className="uppercase text-[10px] tracking-wider text-white/40">{n.type}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Small save-status indicator so agents get instant feedback on whether their
// edit has actually persisted. Mirrors the Leads-list card behavior.
function SaveStatus({ state }) {
  if (state === 'typing') {
    return <span className="text-[10px] text-white/40">Editing…</span>;
  }
  if (state === 'saving') {
    return (
      <span className="text-[10px] text-white/60 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Saving
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="text-[10px] text-emerald-300/80 flex items-center gap-1">
        <Check className="w-3 h-3" /> Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="text-[10px] text-red-300/80 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> Save failed
      </span>
    );
  }
  return null;
}
